import React, { useEffect, useMemo, useRef, useState } from "react";

const CALENDLY_URL = "https://calendly.com/aaron-s-pay2pee/30min";

export default function LandingShell({
  theme = "dark",
  brandTag = "",
  headlineTop = "",
  headlineAccent = "",
  subheadline = "",
  pills = [],
  stripItems = [],
  howSteps = [],
  benefits = [],
  faq = [],
  ctaTitle = "",
  ctaText = "",
  ctaBullets = [],
  primaryCtaText = "Become a Partner",
  scenarioLabel = "Scenarios",
  scenarios = null,
}) {
  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;

    const scroller =
      el.closest(".nl")?.parentElement ||
      document.scrollingElement ||
      document.documentElement;

    const headerOffset = 84;

    const elTop = el.getBoundingClientRect().top;
    const isWindowScroll =
      scroller === document.scrollingElement ||
      scroller === document.documentElement;

    if (isWindowScroll) {
      const y = window.pageYOffset + elTop - headerOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    } else {
      const scrollerTop = scroller.getBoundingClientRect().top;
      const y = scroller.scrollTop + (elTop - scrollerTop) - headerOffset;
      scroller.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const fireEvent = (name, params = {}) => {
    try {
      if (!window.gtag) return;
      window.gtag("event", name, {
        page_location: window.location.href,
        page_path: window.location.pathname,
        theme,
        ...params,
      });
    } catch {
      // no-op
    }
  };

  // ✅ HashRouter-safe download redirect
  const goToDownload = (source = "unknown") => {
    fireEvent("download_click", { source, destination: "download" });
    window.location.hash = "#/download";
  };

  const [active, setActive] = useState(
    scenarios ? Object.keys(scenarios)[0] : null
  );
  const [videoHint, setVideoHint] = useState(false);
  const videoRef = useRef(null);

  const current = useMemo(() => {
    if (!scenarios || !active) return null;
    return scenarios[active];
  }, [scenarios, active]);

  useEffect(() => {
    if (!scenarios || !videoRef.current || !current) return;
    const v = videoRef.current;

    setVideoHint(false);
    v.pause();
    v.load();
    v.play().catch(() => setVideoHint(true));
  }, [scenarios, current]);

  const handleSignUp = () => {
    goToDownload("landing");
  };

  const handleScheduleDemo = () => {
    fireEvent("schedule_demo_click", { source: "landing", outbound: "calendly" });
    window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <main
      className={`nl ${theme === "light" ? "nlLight" : ""} ${
        theme === "nightlife" ? "nlNightlife" : ""
      }`}
    >
      <header className="nlTopbar">
        <div className="nlContainer nlTopbarInner">
          <div className="nlBrand">
            <img
              src="/images/logo.png"
              alt="Pay2Pee logo"
              className="nlBrandLogo"
            />
            <div>
              <div className="nlBrandName">Pay2Pee</div>
              <div className="nlBrandTag">{brandTag}</div>
            </div>
          </div>

          <nav className="nlNav">
            <button type="button" className="nlNavLink" onClick={() => scrollToId("how")}>
              How it works
            </button>
            <button type="button" className="nlNavLink" onClick={() => scrollToId("benefits")}>
              Benefits
            </button>
            <button type="button" className="nlNavLink" onClick={() => scrollToId("faq")}>
              FAQ
            </button>
            <button
              type="button"
              className="nlBtn nlBtnGhost"
              onClick={() => goToDownload("top_nav")}
            >
              {primaryCtaText}
            </button>
          </nav>
        </div>
      </header>

      <section className="nlHero" id="top">
        <div className="nlContainer nlHeroGrid">
          <div>
            <h1 className="nlH1">
              {headlineTop} <span className="nlBlue">{headlineAccent}</span>
              <br />
              <span className="nlOrange">Without the chaos.</span>
            </h1>

            {subheadline ? <p className="nlSub">{subheadline}</p> : null}

            <div className="nlCtas">
              <button
                type="button"
                className="nlBtn nlBtnPrimary"
                onClick={() => goToDownload("hero")}
              >
                {primaryCtaText}
              </button>

              {scenarios ? (
                <button
                  type="button"
                  className="nlBtn nlBtnSecondary"
                  onClick={() => {
                    fireEvent("watch_scenarios_click", { source: "hero" });
                    scrollToId("video");
                  }}
                >
                  Watch scenarios
                </button>
              ) : (
                <button
                  type="button"
                  className="nlBtn nlBtnSecondary"
                  onClick={() => {
                    fireEvent("learn_how_click", { source: "hero" });
                    scrollToId("how");
                  }}
                >
                  Learn how it works
                </button>
              )}
            </div>

            {!!pills?.length && (
              <div className="nlPills">
                {pills.map((p) => (
                  <span key={p} className="nlPill">{p}</span>
                ))}
              </div>
            )}
          </div>

          {scenarios && current && (
            <div id="video" className="nlCard nlMediaCard">
              <div className="nlMediaTop">
                <div className="nlMediaTitle">{scenarioLabel}</div>

                <div className="nlChips">
                  {Object.keys(scenarios).map((k) => (
                    <button
                      key={k}
                      type="button"
                      className={`nlChip ${active === k ? "active" : ""}`}
                      onClick={() => {
                        fireEvent("scenario_select", { scenario: k });
                        setActive(k);
                      }}
                    >
                      {k}
                    </button>
                  ))}
                </div>
              </div>

              <div className="nlVideoWrap">
                <video
                  ref={videoRef}
                  className="nlVideo"
                  playsInline
                  muted
                  loop
                  preload="metadata"
                  poster={current.poster}
                  controls
                  onError={() => setVideoHint(true)}
                >
                  <source src={current.src} type="video/mp4" />
                </video>

                {videoHint && (
                  <div className="nlVideoHint">
                    Video loading / coming soon — scenarios will play here.
                  </div>
                )}
              </div>

              <div className="nlMediaFooter">
                <div className="nlCaption">{current.caption}</div>
                <button
                  type="button"
                  className="nlLink"
                  onClick={() => goToDownload("video_card")}
                >
                  Get partner info →
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {!!stripItems?.length && (
        <section className="nlStrip">
          <div className="nlContainer nlStripGrid">
            {stripItems.map((it) => (
              <div key={it.kicker} className="nlStripItem">
                <div className="nlKicker">{it.kicker}</div>
                <div className="nlValue">{it.value}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="nlSection" id="how">
        <div className="nlContainer">
          <h2 className="nlH2">How it works</h2>
          <p className="nlMuted">A simple flow that keeps you in control.</p>

          <div className="nlSteps">
            {howSteps.map((s) => (
              <div key={s.title} className="nlStep">
                <div className="nlStepIcon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="nlSection nlAlt" id="benefits">
        <div className="nlContainer">
          <h2 className="nlH2">Benefits</h2>
          <p className="nlMuted">Designed to improve experience and operations.</p>

          <div className="nlGrid2">
            {benefits.map((b) => (
              <div key={b.title} className="nlBox">
                <h3>{b.title}</h3>
                <p>{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="nlSection" id="signup">
        <div className="nlContainer">
          <div className="nlCtaCard">
            <div>
              <h2 className="nlH2">{ctaTitle}</h2>
              <p className="nlMuted">{ctaText}</p>
              <ul className="nlList">
                {ctaBullets.map((x) => <li key={x}>{x}</li>)}
              </ul>
            </div>

            <div className="nlForm" aria-label="Partner call-to-action">
              <button
                type="button"
                className="nlBtn nlBtnPrimary nlBtnFull"
                onClick={() => goToDownload("signup_section")}
              >
                Download Now
              </button>

              <button
                type="button"
                className="nlBtn nlBtnSecondary nlBtnFull"
                onClick={handleScheduleDemo}
              >
                Schedule a demo
              </button>

              <div className="nlTiny nlMuted" style={{ lineHeight: 1.4 }}>
                Download Now opens the app download page. Demo opens Calendly.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="nlSection nlAlt" id="faq">
        <div className="nlContainer">
          <h2 className="nlH2">FAQ</h2>
          <div className="nlFaq">
            {faq.map((q) => (
              <details key={q.q}>
                <summary>{q.q}</summary>
                <p>{q.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <footer className="nlFooter">
        <div className="nlContainer nlFooterInner">
          <div className="nlMuted">© {new Date().getFullYear()} Pay2Pee</div>
          <div className="nlFooterLinks">
            <button type="button" className="nlLink" onClick={() => goToDownload("footer")}>
              Partner signup
            </button>
            <button type="button" className="nlLink" onClick={() => scrollToId("how")}>
              How it works
            </button>
            {scenarios ? (
              <button type="button" className="nlLink" onClick={() => scrollToId("video")}>
                Video
              </button>
            ) : null}
          </div>
        </div>
      </footer>
    </main>
  );
}